import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { ASG_CHIP, docApi, docErr } from "@/lib/docApi";
import { btnGold, btnRed, field, when } from "@/components/nds/windows/finance/parts";
import { History, StatusChip } from "@/components/nds/windows/doc/parts";
import { LinkBox, copyLink } from "@/components/nds/windows/doc/AssignForm";

const OPEN = ["pending", "viewed"];

/** SA controls on one assignment: copy link, refresh expired token, void with reason. */
export default function AssignmentDetail({ rec, sa, created, onBack, onChanged }) {
  const [reason, setReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [fresh, setFresh] = useState(created);
  const act = async (fn, msg) => { try { const d = await fn(); toast.success(msg); onChanged(); return d; } catch (e) { toast.error(docErr(e)); return null; } };
  const refresh = async () => { const d = await act(() => docApi.post(`/assignments/${rec.id}/refresh`), "New 72-hour link issued"); if (d) setFresh(d); };
  const copy = () => docApi.get(`/assignments/${rec.id}/link`).then((d) => copyLink(d.link)).catch((e) => toast.error(docErr(e)));
  return (
    <div data-testid="doc-asg-detail" className="p-5 space-y-4">
      <button type="button" onClick={onBack} data-testid="doc-asg-back" className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold"><ArrowLeft className="h-3 w-3" /> All assignments</button>
      <div className="flex flex-wrap items-center gap-3">
        <h3 data-testid="doc-asg-detail-title" className="text-lg text-nex-silver font-semibold">{rec.document_title}</h3>
        <StatusChip map={ASG_CHIP} status={rec.status} testid="doc-asg-detail-status" />
      </div>
      <p className="text-sm text-nex-muted">{rec.recipient_name} · {rec.recipient_type} · {rec.assignment_type.replace(/_/g, " ")}{rec.recipient_email ? ` · ${rec.recipient_email}` : ""}</p>
      <p className="text-xs text-nex-muted">Link expires {when(rec.token_expires_at)} · sent {when(rec.sent_at)}</p>
      <p data-testid="doc-asg-email-status" className="text-xs text-nex-muted">
        24h reminder: {rec.reminder_sent_at ? `${rec.reminder_status} ${when(rec.reminder_sent_at)}` : "not sent yet"}
        {rec.signed_copy_email_status ? ` · Signed copy email: ${rec.signed_copy_email_status}` : ""}
      </p>
      {fresh?.link && <LinkBox link={fresh.link} emailSent={fresh.email_sent} prefix="doc-asg" />}
      {sa && (
        <div className="flex flex-wrap gap-2 items-center">
          {OPEN.includes(rec.status) && <button type="button" onClick={copy} data-testid="doc-asg-copy" className={btnGold}>Copy link</button>}
          {rec.status === "expired" && <button type="button" onClick={refresh} data-testid="doc-asg-refresh" className={btnGold}>Refresh link</button>}
          {[...OPEN, "expired"].includes(rec.status) && (voiding
            ? <><input data-testid="doc-asg-void-reason" className={field} placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <button type="button" disabled={!reason.trim()} onClick={() => act(() => docApi.post(`/assignments/${rec.id}/void`, { reason }), "Assignment voided")} data-testid="doc-asg-void-confirm" className={btnRed}>Confirm void</button></>
            : <button type="button" onClick={() => setVoiding(true)} data-testid="doc-asg-void" className={btnRed}>Void</button>)}
        </div>
      )}
      {rec.void_reason && <p data-testid="doc-asg-void-text" className="text-sm text-red-300">Voided: {rec.void_reason}</p>}
      <History rec={rec} testid="doc-asg-history" />
    </div>
  );
}
